/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

/* eslint-disable no-unsafe-optional-chaining */

import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  ActionMenu,
  ActionMenuProps,
  BADGE_TYPE_STRONG,
  Badge,
  DIALOG_SIZE_SM,
  DROPDOWN_POSITION_RIGHT,
  DialogContentProps,
  DialogProps,
  DropdownItemData,
  EmptyState,
  EmptyStateProps,
  ICON_DONUT_SMALL_WITH_LIST,
  SPINNER_TYPE_REGULAR,
  Spinner,
} from '@squiz/sds';
import { EntityDxpUserMeOrganisation } from 'apps/console-core/src/Services/console';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import { useSegmentGroups } from 'apps/customer-data-platform/src/Services/segmentation/src/Hooks/UseSegmentGroups/UseSegmentGroups.hook';
import { useConsoleServiceStore, useDialogStore } from 'dxpConsoleCore-mf/State';
import property from 'lodash/property';
import React, { KeyboardEvent, MouseEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { EntitySegment, EntitySegmentGroupList, useEvents, useSegments } from '../../../../Services/segmentation';
import { getSegmentPath } from '../../../../Utilities/GetSegmentPath/GetSegmentPath';
import { SEGMENTATION_DATA_IDS } from '../../Segmentation.constants';
import { CreateSegmentButton } from '../CreateSegmentButton/CreateSegmentButton';
import { MAX_CHARACTERS } from './SegmentsList.constants';
import { DeleteSegmentDialog } from './elements/DeleteSegmentDialog/DeleteSegmentDialog';
import { DeleteSegmentDialogProps } from './elements/DeleteSegmentDialog/DeleteSegmentDialog.props';
import { SuccessMetricDialog } from './elements/SuccessMetricDialog/SuccessMetricDialog';

// A specific UI type that combines both group and segment items.
export interface SegmentListUIItem extends EntitySegment {
  isGroup: boolean;
}

/**
 * Merges groups and segments into a single array for display in the UI.
 * @param segments List of segments keyed by segment id.
 * @param groups List of groups keyed by group id.
 * @returns Prepared array for the UI.
 */
export const mergeGroupsToSegmentList = (
  segments: Record<string, EntitySegment>,
  groups: EntitySegmentGroupList,
): Array<SegmentListUIItem> => {
  const list: Array<SegmentListUIItem> = [];
  let groupedSegments: Array<string> = [];
  Object.entries(groups).forEach(([, group]): void => {
    groupedSegments = [...groupedSegments, ...group.segments];
    list.push({
      code: property(`${group.segments[0]}.code`)(segments) as string, // Not used.
      conditions: {}, // Not used.
      description: group.segments
        .map((segmentId): string => property(`${segmentId}.name`)(segments) as string)
        .join(`, `),
      internalId: group.segments[0] as string,
      isGroup: true,
      name: group.name,
    });
  });
  Object.entries(segments)
    .filter(([segmentId]): boolean => {
      return groupedSegments.indexOf(segmentId) === -1;
    })
    .forEach(([, segment]) => {
      list.push({
        ...segment,
        isGroup: false,
      });
    });
  return list.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
};

export const SegmentsList = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [cacheKey, setCacheKey] = useState<string>(``);
  const { tenant }: { tenant: EntityDxpUserMeOrganisation | null } = useConsoleServiceStore();
  const segmentList = useSegments(cacheKey);
  const segmentLookup = useMemo((): Record<string, EntitySegment> => {
    if (segmentList) {
      return segmentList.reduce((prev, curr): Record<string, EntitySegment> => {
        return {
          ...prev,
          [curr.internalId as string]: curr,
        };
      }, {});
    }
    return {};
  }, [segmentList]);
  const groupList = useSegmentGroups(cacheKey);
  const eventList = useEvents(cacheKey);
  const [query, setQuery] = useSearchParams();

  const groupedSegments = useMemo((): Array<SegmentListUIItem> => {
    if (groupList && segmentLookup) {
      return mergeGroupsToSegmentList(segmentLookup, groupList);
    }
    return [];
  }, [groupList, segmentLookup]);

  const { updateActiveDialog } = useDialogStore();

  const selectedSegment = useMemo((): null | EntitySegment => {
    const selectedSegmentCode = query.get(`segment`);
    if (!segmentList) {
      return null;
    }
    const selected = segmentList.filter((segment) => segment.code === selectedSegmentCode).pop();
    if (!selected) {
      return null;
    }
    return selected;
  }, [query, segmentList]);

  const navigateToSegment = useCallback(
    (segmentPath: string): void => {
      navigate({ pathname: segmentPath });
    },
    [navigate],
  );

  // Labels
  const labels = useMemo(() => {
    return {
      actionMenuAriaLabel: t(CDP_STRINGS.segmentActionMenuAriaLabel),
      addSuccessMetric: t(CDP_STRINGS.addSuccessMetric),
      cancel: t(CDP_STRINGS.cancel),
      deleteSegment: t(CDP_STRINGS.segmentActionMenuDelete),
      deleteSegmentDialogHeading: t(CDP_STRINGS.deleteSegmentDialogHeading),
      editSuccessMetric: t(CDP_STRINGS.editSuccessMetric),
      eventFeedLabel: t(CDP_STRINGS.eventFeedLabel),
      noSegmentsCaption: t(CDP_STRINGS.noSegmentsCaption),
      successMetricAddedDescriptionOne: t(CDP_STRINGS.successMetricAddedDescriptionOne),
      successMetricAddedDescriptionTwo: t(CDP_STRINGS.successMetricAddedDescriptionTwo),
      successMetricAddedTitle: t(CDP_STRINGS.successMetricAddedTitle),
      toastEventSuccessUpdatedTitle: t(CDP_STRINGS.toastEventSuccessUpdatedTitle),
      viewSegment: t(CDP_STRINGS.segmentActionMenuViewDetails),
    };
  }, [t]);

  const canCreate = hasPermission(PERMISSIONS.dxpSegmentation.SEGMENT_CREATE);

  // Truncate lengthy text and add ellipsis
  const truncateText = (text: string, maxChars = MAX_CHARACTERS): string => {
    const textTrim = text.trim();
    return text.length > maxChars ? text.substring(0, maxChars) + '...' : textTrim;
  };

  // Resets selected segment and the list back to one fetched from the server.
  const resetSegments = useCallback((): void => {
    setQuery({ segment: `` });
    setCacheKey(new Date().toISOString());
  }, [setCacheKey, setQuery]);

  if (!tenant || !segmentList || !eventList) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  const getSegmentList = (): JSX.Element => {
    const canDeleteSegment = hasPermission(PERMISSIONS.dxpSegmentation.SEGMENT_DELETE);
    return (
      <ul className="segment-list">
        {groupedSegments &&
          groupedSegments.map((segment, index) => {
            const { description, name, internalId, isGroup } = segment;
            let groupId: null | string = null;
            if (isGroup && groupList) {
              Object.entries(groupList).forEach(([gid, group]) => {
                if (group.segments.includes(internalId as string)) {
                  groupId = gid;
                }
              });
            }

            const titleToDisplay = truncateText(name);
            const descriptionToDisplay = truncateText(description);

            const currentScreenPath = getCapabilityPath(`/segmentation`);
            const segmentPath = `${currentScreenPath}${getSegmentPath(`/`, internalId)}`;

            const actionMenuSelector = `[data-id="${SEGMENTATION_DATA_IDS.segmentActionMenuButton}"]`;

            const goToSegmentMouseHandler = (event: MouseEvent<HTMLDivElement>): void => {
              const actionMenuButton = (event.target as HTMLDivElement).closest(actionMenuSelector);
              // Only navigate to segment if the segment item itself is clicked
              // and not the action menu.
              if (!actionMenuButton) {
                navigateToSegment(segmentPath);
              }
            };

            const goToSegmentKeyboardHandler = (event: KeyboardEvent<HTMLDivElement>): void => {
              const actionMenuButton = (event.target as HTMLDivElement).closest(actionMenuSelector);
              // Keys which should navigate user to the segment.
              const navigateKeys = ['Enter', 'Space'];
              const shouldNavigate = !actionMenuButton && navigateKeys.includes(event.code);
              if (shouldNavigate) {
                navigateToSegment(segmentPath);
              }
            };

            // Ellipsis action menu for the segment item
            const goToSegmentViaMenu = (): void => navigateToSegment(segmentPath);

            const dialogProps: DialogProps = {
              dialogContent: {
                renderContent: (props: DialogContentProps): JSX.Element => {
                  const dialogProps: DeleteSegmentDialogProps = {
                    ...props,
                    clearCache: setCacheKey,
                    id: isGroup ? (groupId as string) : (internalId as string),
                    isGroup,
                    name,
                  };

                  return <DeleteSegmentDialog {...dialogProps} />;
                },
              },
              dialogSize: DIALOG_SIZE_SM,
              heading: labels.deleteSegmentDialogHeading,
              preventFullScreen: true,
              scrollOnOverflow: false,
              stateHandler: useDialogStore,
            };

            const menuItems: Array<DropdownItemData> = [
              {
                action: (): void => {
                  setQuery({ segment: segment.code });
                },
                dataID: SEGMENTATION_DATA_IDS.segmentActionMenuAddSuccessButton,
                key: `0`,
                label:
                  property(`success`)(segment) && Object.keys(property(`success`)(segment) as object).length >= 1
                    ? labels.editSuccessMetric
                    : labels.addSuccessMetric,
              },
              {
                action: goToSegmentViaMenu,
                dataID: SEGMENTATION_DATA_IDS.segmentActionMenuItemViewDetails,
                key: `1`,
                label: labels.viewSegment,
              },
            ];

            if (canDeleteSegment) {
              // Delete segment menu item
              menuItems.push({
                action: (): void => updateActiveDialog(dialogProps),
                dataID: SEGMENTATION_DATA_IDS.segmentActionMenuItemDelete,
                key: `2`,
                label: labels.deleteSegment,
              });
            }

            const actionMenuProps: ActionMenuProps = {
              buttonLabel: labels.actionMenuAriaLabel,
              classes: `segment-item__menu`,
              dropdownPosition: DROPDOWN_POSITION_RIGHT,
              itemList: menuItems,
              sectionKey: SEGMENTATION_DATA_IDS.segmentActionMenuButton,
            };

            return (
              <li data-id={SEGMENTATION_DATA_IDS.segmentItem} id={internalId} key={index}>
                <div
                  className="segment-item"
                  onClick={goToSegmentMouseHandler}
                  onKeyDown={goToSegmentKeyboardHandler}
                  role="link"
                  tabIndex={0}
                >
                  <div className="segment-item__details">
                    <div className="segment-item__title" data-id={SEGMENTATION_DATA_IDS.segmentItemTitle}>
                      <span>{titleToDisplay}</span>
                      <span>{isGroup && <Badge left="Mutually exclusive group" type={BADGE_TYPE_STRONG} />}</span>
                    </div>
                    {descriptionToDisplay !== `` && (
                      <div className="segment-item__description" data-id={SEGMENTATION_DATA_IDS.segmentItemDescription}>
                        {descriptionToDisplay}
                      </div>
                    )}
                  </div>
                  <div className="segment-item__action-menu">
                    <ActionMenu {...actionMenuProps} data-id={SEGMENTATION_DATA_IDS.segmentActionMenuButton} />
                  </div>
                </div>
              </li>
            );
          })}
      </ul>
    );
  };

  const hasSegments = Object.keys(segmentList).length > 0;
  if (hasSegments) {
    return (
      <>
        {selectedSegment !== null && (
          <SuccessMetricDialog
            eventList={eventList}
            resetSegments={resetSegments}
            segmentList={segmentList}
            selectedSegment={selectedSegment}
          />
        )}
        <div className="segment-list__actions">
          <CreateSegmentButton />
        </div>
        {getSegmentList()}
      </>
    );
  }

  const emptyStateClass = canCreate ? `sds-empty-state-card--with-cta` : ``;
  const emptyStateProps: EmptyStateProps = {
    caption: labels.noSegmentsCaption,
    classes: emptyStateClass,
    dataId: SEGMENTATION_DATA_IDS.segmentsListEmptyState,
  };

  return (
    <EmptyState {...emptyStateProps} svg={ICON_DONUT_SMALL_WITH_LIST}>
      {canCreate && <CreateSegmentButton />}
    </EmptyState>
  );
};
